import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import { newsArticlePath, type NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'consumo-hosteleria-presupuesto-hogares-2026'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    ineEpf: {
        href: 'https://www.ine.es/dyngs/Prensa/EPF2025.htm',
        domain: 'ine.es',
        label: 'INE · EPF 2025',
    },
    ineHogares: {
        href: 'https://www.ine.es/dyngs/Prensa/PROH20242039.htm',
        domain: 'ine.es',
        label: 'INE · Proyección de hogares',
    },
    hosteleriaMadrid: {
        href: 'https://www.hosteleriamadrid.com/blog/el-consumo-fuera-del-hogar-primer-trimestre-2026/',
        domain: 'hosteleriamadrid.com',
        label: 'Hostelería Madrid',
    },
    theobjective: {
        href: 'https://theobjective.com/economia/2026-09-07/pais-bares-hosteleria-principal-motor-consumo-espana/',
        domain: 'theobjective.com',
        label: 'The Objective (Eurostat)',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function ConsumoHosteleriaPresupuestoArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'bolsillo', label: t(k('toc.bolsillo'), '1.057 € más en el bolsillo, 92 menos en tu caja') },
        { id: 'nevera', label: t(k('toc.nevera'), '¿A dónde se ha ido ese dinero?') },
        { id: 'turista-vs-vecino', label: t(k('toc.turista'), 'El 18,1 % habla del turista, no de tu vecino') },
        { id: 'por-que-baja', label: t(k('toc.porQue'), 'Por qué baja el consumo si la gente sigue saliendo') },
        { id: 'reparto', label: t(k('toc.reparto'), 'Reparte esos 92 euros entre 300.000 persianas') },
        { id: 'mesa', label: t(k('toc.mesa'), 'La mesa te cuesta lo mismo aunque pidan la mitad') },
        { id: 'carta-invierno', label: t(k('toc.carta'), 'Qué mirar antes de cerrar la carta de invierno') },
        { id: 'limites', label: t(k('toc.limites'), 'Lo que este dato no cubre') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del consumo en hostelería y el presupuesto de los hogares y me gustaría separar comida y bebida en mis números.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>
                {t(k('weekActions.1.strong'), 'Separa tu venta en comida y bebida, mes a mes, de los dos últimos años.')}
            </strong>{' '}
            {t(k('weekActions.1.after'), 'Si la bebida ha perdido peso, tienes localizado el agujero y no es de afluencia.')}
        </>,
        <>
            <strong>{t(k('weekActions.2.strong'), 'Mira los tickets por franja.')}</strong>{' '}
            {t(k('weekActions.2.after'), 'Si el mediodía aguanta y la noche cae, te está pasando lo mismo que al panel.')}
        </>,
        <>
            <strong>
                {t(k('weekActions.3.strong'), 'Calcula el margen en euros, no en porcentaje, de tus cinco bebidas más vendidas.')}
            </strong>{' '}
            {t(k('weekActions.3.after'), 'El porcentaje casi no se mueve cuando cae la venta; los euros sí.')}
        </>,
        <>
            <strong>
                {t(
                    k('weekActions.4.strong'),
                    'Escandalla los platos que han ganado peso este año antes de imprimir la carta de invierno.',
                )}
            </strong>{' '}
            {t(k('weekActions.4.after'), 'Los eligió el cliente, no tú.')}
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'INE, Encuesta de Presupuestos Familiares 2025, resultados definitivos, publicada el 25 de junio de 2026',
            ),
            href: REF.ineEpf.href,
        },
        {
            label: t(
                k('sources.2'),
                'INE, Proyección de hogares 2024-2039, 19.310.776 hogares a 1 de enero de 2024',
            ),
            href: REF.ineHogares.href,
        },
        {
            label: t(
                k('sources.3'),
                'Panel de consumo fuera del hogar del Ministerio de Agricultura, Pesca y Alimentación, primer trimestre de 2026, recogido por Hostelería Madrid',
            ),
            href: REF.hosteleriaMadrid.href,
        },
        {
            label: t(
                k('sources.4'),
                'The Objective, 7 de septiembre de 2026, citando la serie de Eurostat sobre el peso de la hostelería en el gasto de los hogares',
            ),
            href: REF.theobjective.href,
        },
        {
            label: t(k('sources.5'), 'Anuario de Hostelería de España, más de 300.000 establecimientos'),
            href: REF.anuario.href,
        },
    ]

    const ownSource = t(
        k('sources.6'),
        'Reparto por establecimiento y ejemplo de mesa: elaboración propia de Tipi',
    )

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforeTotal'), 'El gasto medio por hogar en España subió ')}
                            <strong>{t(k('dek.totalUp'), '1.057 euros')}</strong>
                            {t(k('dek.afterTotal'), ' en 2025, hasta los ')}
                            <strong>{t(k('dek.total'), '35.101')}</strong>
                            {t(k('dek.afterTotalNum'), '. El de restaurantes y alojamiento cayó un ')}
                            <strong>{t(k('dek.hospitalityDrop'), '2,7 %')}</strong>
                            {t(k('dek.afterDrop'), ', ')}
                            <strong>{t(k('dek.eurosLess'), '92 euros menos')}</strong>
                            {t(
                                k('dek.afterEuros'),
                                ' por hogar, según los resultados definitivos de la Encuesta de Presupuestos Familiares que el INE publicó el 25 de junio de 2026. Ese mismo año la comida para casa se llevó ',
                            )}
                            <strong>{t(k('dek.homeFood'), '236 euros más')}</strong>
                            {t(
                                k('dek.afterHomeFood'),
                                ' por hogar. Y lo que se cae dentro de tu local no es la comida: es la bebida, con la cerveza concentrando el ',
                            )}
                            <strong>{t(k('dek.beerShare'), '94,1 %')}</strong>
                            {t(
                                k('dek.afterBeer'),
                                ' de la pérdida. Esa es la razón de fondo por la que baja el consumo en hostelería, y no tiene nada que ver con que la gente tenga menos dinero.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Barra de bar con cañas a medias y un ticket de caja, sin clientes a la vista',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='bolsillo' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.bolsillo'), '1.057 euros más en su bolsillo, 92 menos en tu caja')}
                            </h2>
                            <p className={article.p}>
                                {t(k('bolsillo.p1'), 'Mil cincuenta y siete euros más al año.')}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('bolsillo.p2.before'),
                                        'Eso creció el presupuesto de un hogar español medio en 2025, según la ',
                                    )}
                                    <a
                                        href={REF.ineEpf.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('bolsillo.p2.link'), 'Encuesta de Presupuestos Familiares del INE')}
                                    </a>
                                    <SourceChip source='ineEpf' />
                                </span>
                                {t(k('bolsillo.p2.after'), '. No es una sensación: 24.000 hogares encuestados, resultados definitivos.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(k('bolsillo.p3'), 'De ese dinero nuevo, a ti no te llegó nada. Al revés.')}
                                </strong>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <div className={article.tableWrap}>
                        <table className={article.tableFacts}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.group'), 'Grupo de gasto')}</th>
                                    <th scope='col'>{t(k('table.col.home'), 'Hogar medio en 2025')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th scope='row'>{t(k('table.total.label'), 'Gasto total')}</th>
                                    <td>
                                        <strong>{t(k('table.total.value'), '35.101 €, +3,1 %')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        {t(k('table.food.label'), 'Alimentos y bebidas no alcohólicas')}
                                    </th>
                                    <td>
                                        <strong>{t(k('table.food.value'), '5.626 €, +4,4 %')}</strong>
                                        {t(k('table.food.after'), ' (236 € más)')}
                                    </td>
                                </tr>
                                <tr>
                                    <th scope='row'>
                                        {t(k('table.hospitality.label'), 'Restaurantes y servicios de alojamiento')}
                                    </th>
                                    <td>
                                        <strong>{t(k('table.hospitality.value'), '3.282 €, −2,7 %')}</strong>
                                        {t(k('table.hospitality.after'), ' (92 € menos)')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={article.prose}>
                        <p className={article.p}>
                            <strong>
                                {t(
                                    k('bolsillo.p4.strong'),
                                    'Solo cuatro de los doce grupos de gasto bajaron en 2025.',
                                )}
                            </strong>{' '}
                            {t(
                                k('bolsillo.p4.after'),
                                'Restaurantes y alojamiento fue el segundo que más cayó, por detrás de alcohol y tabaco.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('bolsillo.p5.before'),
                                'El INE aclara además que la caída se produce ',
                            )}
                            <em>
                                {t(
                                    k('bolsillo.p5.quote'),
                                    '"debido a la bajada del gasto en sus dos componentes"',
                                )}
                            </em>
                            {t(
                                k('bolsillo.p5.after'),
                                '. No es que el hotel arrastre a la restauración. Bajan los dos.',
                            )}
                        </p>
                    </div>

                    <section id='nevera'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.nevera'), '¿A dónde se ha ido ese dinero? A la nevera de casa')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('nevera.p1'),
                                    '236 euros más por hogar en comida para casa. 92 menos en comer y dormir fuera.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('nevera.p2'),
                                        'El español no ha dejado de gastar en comida. Ha decidido dónde se la come.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('nevera.p3'),
                                    'Y esto no es un mal año. Es una reasignación del presupuesto familiar, mucho más lenta de revertir que una mala temporada. El titular de que el gasto en hostelería "se congela" se queda corto: no se congela, cae, y cae en el año en que todo lo demás sube.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='turista-vs-vecino'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.turista'), 'El titular del 18,1 % habla del turista, no de tu vecino')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('turista.p1'),
                                    'Esta misma semana ha circulado otro titular que dice lo contrario: que la hostelería ya es el primer gasto de los hogares españoles.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('turista.p2.before'), 'Y es cierto, según Eurostat. ')}
                                    <a
                                        href={REF.theobjective.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('turista.p2.link'),
                                            'Restaurantes y alojamiento son el 18,1 % del gasto de los hogares en España en 2026',
                                        )}
                                    </a>
                                    <SourceChip source='theobjective' />
                                </span>
                                {t(k('turista.p2.after'), ', por delante de la alimentación, en el 16,9 %.')}
                            </p>
                            <p className={article.p}>
                                {t(k('turista.p3.before'), 'El INE dice ')}
                                <strong>{t(k('turista.p3.pct'), '9,3 %')}</strong>
                                {t(k('turista.p3.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('turista.p4'),
                                    'Nueve puntos entre dos estadísticas oficiales que se llaman igual. El INE encuesta a los hogares que viven aquí; la serie de Eurostat recoge el gasto realizado en el territorio, con el turista dentro.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('turista.p5'),
                                        'Cuando leas que la hostelería es el motor del consumo en España, están hablando del turista.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('turista.p5.after'),
                                    'Tu vecino está en la columna del 9,3 %, y esa columna baja.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('turista.p6.before'),
                                    'Si tu caja depende del de casa, ese es tu dato. El del turista lo desglosamos en ',
                                )}
                                <LocaleLink
                                    to={newsArticlePath('turista-gasta-menos-restaurantes-2026')}
                                    className={article.link}
                                >
                                    {t(k('turista.p6.link'), 'lo que dejó de verdad el gasto turístico de este julio')}
                                </LocaleLink>
                                {t(k('turista.p6.after'), ', y tampoco es lo que parece.')}
                            </p>
                        </div>
                    </section>

                    <section id='por-que-baja'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(
                                k('h2.porQue'),
                                '¿Por qué baja el consumo en hostelería si la gente no ha dejado de salir?',
                            )}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('porQue.p1.before'),
                                        'Primero lo que duele. Lo que ha caído no es la comida: son las consumiciones. Según el panel de consumo del Ministerio de Agricultura en el primer trimestre de 2026, ',
                                    )}
                                    <a
                                        href={REF.hosteleriaMadrid.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('porQue.p1.link'), 'recogido por Hostelería Madrid')}
                                    </a>
                                    <SourceChip source='hosteleriaMadrid' />
                                </span>
                                {t(k('porQue.p1.mid'), ', los alimentos crecieron un ')}
                                <strong>{t(k('porQue.p1.food'), '1,2 % en volumen')}</strong>
                                {t(k('porQue.p1.and'), ' y las bebidas cayeron un ')}
                                <strong>{t(k('porQue.p1.drinks'), '3,4 %')}</strong>
                                {t(k('porQue.p1.end'), '.')}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('porQue.p2'),
                                        'La cerveza concentra el 94,1 % de toda la pérdida en bebidas.',
                                    )}
                                </strong>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p3'),
                                    'El cliente sigue comiendo fuera, pero ha dejado la caña de después, el aperitivo del sábado y la segunda ronda. Justo la parte de tu carta con más margen.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('porQue.p4.before'), 'Y ahora la parte buena, que también es real: ')}
                                <strong>
                                    {t(
                                        k('porQue.p4.strong'),
                                        'la penetración del consumo fuera del hogar subió 1,3 puntos, hasta el 91,4 %.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('porQue.p4.after'),
                                    'Lo que baja es la frecuencia, un 3,1 % menos de actos, y las consumiciones por acto, un 4,4 % menos.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(k('porQue.p5'), 'No has perdido clientes. Has perdido visitas y rondas.')}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='reparto'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.reparto'), 'Reparte esos 92 euros entre 300.000 persianas')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('reparto.p1.before'), 'España tiene ')}
                                    <strong>{t(k('reparto.p1.homes'), 'más de 19,3 millones de hogares')}</strong>
                                    {t(k('reparto.p1.mid'), ', según la proyección del INE')}
                                    <SourceChip source='ineHogares' />
                                </span>
                                {t(k('reparto.p1.and'), ', y ')}
                                <span className={article.cite}>
                                    <strong>
                                        {t(k('reparto.p1.venues'), 'más de 300.000 establecimientos de hostelería')}
                                    </strong>
                                    {t(k('reparto.p1.anuario'), ', según el Anuario de Hostelería de España')}
                                    <SourceChip source='anuario' />
                                </span>
                                {t(k('reparto.p1.end'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(k('reparto.p2.before'), '92 euros menos por hogar son unos ')}
                                <strong>{t(k('reparto.p2.total'), '1.776 millones de euros')}</strong>
                                {t(k('reparto.p2.mid'), ' que han salido del sector en un año. Repartidos entre todos los locales, ')}
                                <strong>{t(k('reparto.p2.perVenue'), '5.900 euros menos al año cada uno')}</strong>
                                {t(k('reparto.p2.after'), ', unos 490 al mes. ')}
                                <em>
                                    {t(
                                        k('reparto.p2.note'),
                                        'Cálculo propio de reparto lineal sobre los datos del INE.',
                                    )}
                                </em>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('reparto.p3'),
                                    'Dos avisos honestos. De ese grupo también sale el alojamiento, así que lo que toca a bares y restaurantes es menos. Y esos 490 euros son de venta, no de margen: con el 3 % o 4 % de margen neto del que hablan hoy las asociaciones del sector, dejan unos 15 o 20 euros de beneficio al mes.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('reparto.p4'),
                                        'Nadie cierra por perder 490 euros de venta al mes. Se cierra por perderlos veinticuatro meses seguidos sin haberlo visto en ningún número.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='mesa'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.mesa'), 'La mesa te cuesta lo mismo aunque pidan la mitad')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('mesa.disclaimer'),
                                        'Estimación propia con una materia prima del 33 % en comida y del 22 % en bebida, más 18 euros de coste de servir la mesa entre personal y local. No es un dato de mercado.',
                                    )}
                                </em>
                            </p>
                            <p className={article.p}>
                                {t(k('mesa.p1'), 'Una mesa de dos, un viernes, noventa minutos.')}
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.table}>
                                <thead>
                                    <tr>
                                        <th scope='col'>{t(k('mesa.table.col1'), 'Concepto')}</th>
                                        <th scope='col'>{t(k('mesa.table.col2'), 'Antes → ahora')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{t(k('mesa.table.food'), 'Comida')}</td>
                                        <td>
                                            {t(k('mesa.table.food.before'), '38 € → ')}
                                            <strong>{t(k('mesa.table.food.after'), '28 €')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('mesa.table.drink'), 'Bebida')}</td>
                                        <td>
                                            {t(k('mesa.table.drink.before'), '24 € → ')}
                                            <strong>{t(k('mesa.table.drink.after'), '6 €')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('mesa.table.sales'), 'Venta')}</td>
                                        <td>
                                            {t(k('mesa.table.sales.before'), '62 € → ')}
                                            <strong>{t(k('mesa.table.sales.after'), '34 €')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('mesa.table.gross'), 'Margen bruto')}</td>
                                        <td>{t(k('mesa.table.gross.value'), '44,20 € → 23,40 €')}</td>
                                    </tr>
                                    <tr className={article.tableTotal}>
                                        <td>
                                            <strong>{t(k('mesa.table.after'), 'Después de pagar la mesa')}</strong>
                                        </td>
                                        <td>
                                            <strong>{t(k('mesa.table.after.value'), '26,20 € → 5,40 €')}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('mesa.p2.before'), 'La venta cae un 45 %. Lo que queda después de pagar esa mesa cae un ')}
                                <strong>{t(k('mesa.p2.pct'), '79 %')}</strong>
                                {t(k('mesa.p2.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('mesa.p3'),
                                    'Porque la mesa ocupa lo mismo, el camarero cuesta lo mismo y el local vale lo mismo. Lo que se ha caído es la bebida, que en esa cuenta era el 39 % del ticket y el ',
                                )}
                                <strong>{t(k('mesa.p3.margin'), '42 % del margen')}</strong>
                                {t(k('mesa.p3.end'), '.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('mesa.p4'),
                                        'Por eso hay comedores llenos que no dan dinero. El aforo no ha bajado. La composición de la cuenta sí.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='carta-invierno'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.carta'), 'Qué mirar antes de cerrar la carta de invierno')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={weekActions} />
                        </div>
                    </section>

                    <section id='limites'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.limites'), 'Lo que este dato no cubre')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('limites.p1.strong'),
                                        'La EPF es de 2025 y se publicó en junio de 2026.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('limites.p1.after'),
                                    'Es la foto más fiable del presupuesto familiar, pero no es la de este mes. Si 2026 corrige la tendencia, no lo sabremos hasta junio de 2027.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('limites.p2.strong'),
                                        'El 18,1 % de Eurostat y el 9,3 % del INE conviven y los dos son oficiales.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('limites.p2.after'),
                                    'Nuestra lectura es que la diferencia está en el turista y el alojamiento, pero ninguna de las dos fuentes lo explica en esos términos. Si aparece el desglose, lo actualizamos aquí.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('limites.p3.strong'),
                                        'Los datos de panel del primer trimestre de 2026 son de muestra, no censo.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('limites.p3.after'),
                                    'Marcan dirección con fiabilidad y magnitud con menos.',
                                )}
                            </p>
                        </div>
                    </section>

                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('closing.p1'),
                                'Si sabes lo que facturas pero no qué parte viene de comida y qué parte de bebida, ni cuánto margen en euros te deja cada una, no puedes saber si este año te ha pasado esto. Cuéntanoslo y lo miramos contigo.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('closing.p2'), 'Seguimos contando lo que a otros no les interesa contar.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sabes cuánto margen en euros te deja la bebida este año?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Revisamos contigo la separación comida/bebida y el margen real de lo que más se ha caído.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
